import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { useSnackbar } from 'notistack';
import { MutableRefObject, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useLoggedInUserRoles } from '~/components/Auth';
import {
  SavedReportSettings,
  SavedReportSettingsPrivacyType,
  UpdatableSavedReportSettingsData,
} from '~/data/reportSettings';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import {
  UseReportDatasetOutput,
  UseSavedReportSettingsConfigOutput,
} from '~/scenes/PortalModules/Analytics/common';
import {
  IncludedSavedSettings,
  canSaveSharedSettingsUserRoles,
  getCurrentDataGridSettings,
  getInitialFormSavedSettings,
} from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/data';
import { hasOneOfRoles } from '~/services/Permissions';
import { browserLogError } from '~/utils/helper';

export enum SavedSettingsUpdateState {
  Excluded = 'excluded',
  Changed = 'changed',
  Unchanged = 'unchanged',
}

export type SavedReportSettingsConfig = {
  savedSettings: IncludedSavedSettings;
};

export type SavedReportSettingsForm = Omit<
  UpdatableSavedReportSettingsData,
  'activeColumns'
> &
  SavedReportSettingsConfig;

type PropsType<P> = {
  apiRef: MutableRefObject<GridApiPremium | null>;
  selectedReportSettings?: SavedReportSettings<P>;
  reportDataset: UseReportDatasetOutput<P>;
  isEdit: boolean;
  onClose: () => void;
} & Pick<
  UseSavedReportSettingsConfigOutput<P>,
  'onCreateReportSettings' | 'onUpdateSelectedReportSettings'
>;

export const useSaveReportSettingsDialog = <P>(props: PropsType<P>) => {
  const {
    apiRef,
    selectedReportSettings,
    reportDataset,
    isEdit,
    onClose,
    onUpdateSelectedReportSettings,
    onCreateReportSettings,
  } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const myRoles = useLoggedInUserRoles();

  const access = useMemo(() => {
    const canCreateShared = hasOneOfRoles(
      myRoles,
      canSaveSharedSettingsUserRoles,
    );

    return {
      canCreateShared,
    };
  }, [myRoles]);

  const currentDataGridSettings = getCurrentDataGridSettings(apiRef);

  const validationSchema = yup.object().shape<SavedReportSettingsForm>({
    name: yup
      .string()
      .required(t('laRequired'))
      .test('test', t('newNameMustDifferFromPrevious'), (value) => {
        return !isEdit && !!selectedReportSettings
          ? value !== selectedReportSettings.name
          : true;
      }),
    privacyType: yup
      .string()
      .required(t('laRequired'))
      .oneOf(Object.values(SavedReportSettingsPrivacyType)),
    reportType: yup.string(),
    savedSettings: yup
      .object({
        reportSelection: yup.boolean(),
        columnVisibility: yup.boolean(),
        columnSorting: yup.boolean(),
        columnFiltering: yup.boolean(),
      })
      .defined(),
  });

  const validationResolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const form = useForm<SavedReportSettingsForm>({
    resolver: validationResolver,
    defaultValues: {
      name: '',
      privacyType: SavedReportSettingsPrivacyType.PERSONAL,
      ...selectedReportSettings,
      savedSettings: getInitialFormSavedSettings(
        isEdit,
        selectedReportSettings?.settingsConfig,
      ),
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = form;

  const savedSettingsValues = useWatch({
    control,
    name: [
      'savedSettings.reportSelection',
      'savedSettings.columnVisibility',
      'savedSettings.columnSorting',
      'savedSettings.columnFiltering',
    ],
  });

  const hasSettingsSelected = savedSettingsValues.some((value) => value);
  const hasAllSettingsSelected = !savedSettingsValues.some((value) => !value);

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const { columnVisibility, columnSorting, columnFiltering } =
        currentDataGridSettings;

      const payload = {
        ...data,
        activeColumns: JSON.stringify({
          reportVersion: 2,
          reportComponent: 'datagrid',
          reportSelection: data.savedSettings.reportSelection
            ? reportDataset.reportSelection
            : undefined,
          columnVisibility: data.savedSettings.columnVisibility
            ? columnVisibility
            : undefined,
          columnSorting: data.savedSettings.columnSorting
            ? columnSorting
            : undefined,
          columnFiltering: data.savedSettings.columnFiltering
            ? columnFiltering
            : undefined,
        }),
      };

      const submissionPromise =
        !!selectedReportSettings && isEdit
          ? onUpdateSelectedReportSettings(payload)
          : onCreateReportSettings(payload);

      submissionPromise
        .then(() => {
          enqueueSnackbar(t('change_saved'), { variant: 'success' });
          onClose();
        })
        .catch((err) => {
          enqueueSnackbar(t('laFailedToSaveReportSettings'), {
            variant: 'error',
          });
          browserLogError(t('laFailedToSaveReportSettings'), err);
        });
    });
  }, [
    handleSubmit,
    onClose,
    onCreateReportSettings,
    onUpdateSelectedReportSettings,
    selectedReportSettings,
    reportDataset,
    currentDataGridSettings,
    isEdit,
    t,
    enqueueSnackbar,
  ]);

  const settingsChangeState = useMemo(() => {
    const [
      reportSelectionSelected,
      columnVisibilitySelected,
      columnSortingSelected,
      columnFilteringSelected,
    ] = savedSettingsValues;

    const hasSettingsChanged = (
      isIncluded: boolean,
      dataA?: object,
      dataB?: object,
    ): boolean => {
      const isDataMatch = JSON.stringify(dataA) == JSON.stringify(dataB);

      return isIncluded ? !isDataMatch : false;
    };

    if (!!selectedReportSettings?.settingsConfig) {
      const selectedSettingsConfig = selectedReportSettings.settingsConfig;

      return {
        reportSelection: hasSettingsChanged(
          reportSelectionSelected,
          selectedSettingsConfig.reportSelection,
          reportDataset.reportSelection,
        ),
        columnVisibility: hasSettingsChanged(
          columnVisibilitySelected,
          selectedSettingsConfig.columnVisibility,
          currentDataGridSettings.columnVisibility,
        ),
        columnFiltering: hasSettingsChanged(
          columnFilteringSelected,
          selectedSettingsConfig?.columnFiltering,
          currentDataGridSettings.columnFiltering,
        ),
        columnSorting: hasSettingsChanged(
          columnSortingSelected,
          selectedSettingsConfig?.columnSorting,
          currentDataGridSettings.columnSorting,
        ),
      };
    }

    return {
      reportSelection: true,
      columnVisibility: true,
      columnFiltering: true,
      columnSorting: true,
    };
  }, [
    savedSettingsValues,
    selectedReportSettings,
    currentDataGridSettings,
    reportDataset,
  ]);

  const settingsUpdateState = useMemo(() => {
    const [
      reportSelectionSelected,
      columnVisibilitySelected,
      columnSortingSelected,
      columnFilteringSelected,
    ] = savedSettingsValues;

    const getSettingsChangeState = (
      changeState: boolean,
      selectionState: boolean,
    ): SavedSettingsUpdateState => {
      const currentStateColor = changeState
        ? SavedSettingsUpdateState.Changed
        : SavedSettingsUpdateState.Unchanged;

      return selectionState
        ? currentStateColor
        : SavedSettingsUpdateState.Excluded;
    };

    const updateState = {
      reportSelection: getSettingsChangeState(
        settingsChangeState.reportSelection,
        reportSelectionSelected,
      ),
      columnVisibility: getSettingsChangeState(
        settingsChangeState.columnVisibility,
        columnVisibilitySelected,
      ),
      columnSorting: getSettingsChangeState(
        settingsChangeState.columnSorting,
        columnSortingSelected,
      ),
      columnFiltering: getSettingsChangeState(
        settingsChangeState.columnFiltering,
        columnFilteringSelected,
      ),
    };

    const isUpdated = Object.values(updateState).some(
      (state) => state === SavedSettingsUpdateState.Changed,
    );

    return {
      ...updateState,
      isUpdated,
    };
  }, [settingsChangeState, savedSettingsValues]);

  const isSubmitDisabled =
    !hasSettingsSelected ||
    (isEdit ? !settingsUpdateState.isUpdated && !isDirty : !isDirty);

  return {
    access,
    form,
    settingsSelectionState: {
      savedSettingsValues,
      hasSettingsSelected,
      hasAllSettingsSelected,
    },
    settingsChangeState,
    settingsUpdateState,
    isSubmitDisabled,
    onSubmit,
  };
};
